import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { careLoc } from "@/lib/geo";
import { CareShell } from "@/components/CareShell";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Check } from "lucide-react";

const WINDOWS = ["9am – 12pm", "12pm – 3pm", "3pm – 6pm", "6pm – 8pm"];

export default function CareVisit() {
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const [plans, setPlans] = useState([]);
    const [members, setMembers] = useState([]);
    const [planId, setPlanId] = useState(sp.get("plan") || "");
    const [memberId, setMemberId] = useState("self");
    const [date, setDate] = useState("");
    const [slot, setSlot] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/care/plans").then((r) => { setPlans(r.data); if (!planId && r.data[0]) setPlanId(r.data[0].id); }).catch(() => {});
        api.get("/care/members").then((r) => setMembers(r.data)).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const plan = plans.find((p) => p.id === planId);

    const submit = async () => {
        if (!planId || !date || !slot) return toast.error("Please choose a plan, date and time");
        setBusy(true);
        try {
            const { data } = await api.post("/care/visits", { plan_id: planId, member_id: memberId, date, window: slot, notes, ...careLoc() });
            toast.success(`Care visit requested (${data.care_id})`);
            nav("/care");
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <CareShell title="Book a Care Visit" noNav>
            <div className="flex items-center gap-3 px-5 pt-1">
                <button data-testid="care-book-back" onClick={() => nav(-1)} className="rounded-full bg-navy/5 p-2"><ChevronLeft size={20} className="text-navy" /></button>
                <h1 className="font-heading text-lg font-extrabold text-navy">Book a Care Visit</h1>
            </div>

            <div className="space-y-4 px-5 py-4">
                <Section label="Care plan">
                    <div className="space-y-2">
                        {plans.map((p) => (
                            <button key={p.id} data-testid={`book-plan-${p.id}`} onClick={() => setPlanId(p.id)} className={`flex w-full items-center justify-between rounded-2xl border-2 p-3 text-left transition-colors ${planId === p.id ? "border-[#F43F5E] bg-[#FFF3E8]" : "border-navy/10 bg-white"}`}>
                                <div><p className="font-heading text-sm font-bold text-navy">{p.name}</p><p className="text-xs text-navy/55">{p.duration}</p></div>
                                <span className="font-heading text-sm font-extrabold text-[#F43F5E]">{p.price_label}</span>
                            </button>
                        ))}
                    </div>
                </Section>

                <Section label="Who is this visit for?">
                    <select data-testid="book-member" value={memberId} onChange={(e) => setMemberId(e.target.value)} className="w-full rounded-xl border border-navy/15 bg-white px-3 py-3 text-sm text-navy outline-none focus:border-[#F43F5E]">
                        {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.relation})</option>)}
                    </select>
                </Section>

                <Section label="Preferred date">
                    <input data-testid="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-xl border border-navy/15 bg-white px-3 py-3 text-sm text-navy outline-none focus:border-[#F43F5E]" />
                </Section>

                <Section label="Preferred time">
                    <div className="grid grid-cols-2 gap-2">
                        {WINDOWS.map((w) => (
                            <button key={w} data-testid={`book-window-${w.replace(/[^a-z0-9]/gi, "")}`} onClick={() => setSlot(w)} className={`rounded-xl border-2 py-2.5 text-sm font-semibold transition-colors ${slot === w ? "border-[#F43F5E] bg-[#FFF3E8] text-navy" : "border-navy/10 bg-white text-navy/70"}`}>{w}</button>
                        ))}
                    </div>
                </Section>

                <Section label="Notes for the caregiver (optional)">
                    <textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Any specific needs, access details, medication timings…" className="w-full rounded-xl border border-navy/15 bg-white px-3 py-3 text-sm text-navy outline-none focus:border-[#F43F5E]" />
                </Section>
            </div>

            <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-md border-t border-navy/10 bg-[#FFFAF5] p-4">
                <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="text-navy/55">{plan ? plan.name : "Select a plan"}</span>
                    <span className="font-heading text-base font-extrabold text-navy">{plan ? plan.price_label : "—"}</span>
                </div>
                <button data-testid="book-care-submit" disabled={busy} onClick={submit} className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check size={17} />} REQUEST CARE VISIT
                </button>
                <p className="mt-1.5 text-center text-[11px] text-navy/40">Your Care Manager confirms the caregiver & timing before any payment.</p>
            </div>
        </CareShell>
    );
}

function Section({ label, children }) {
    return <div><p className="mb-2 text-sm font-bold text-navy">{label}</p>{children}</div>;
}
